import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, Button, Spinner, Alert, FormLabel } from "@themesberg/react-bootstrap";
import { postApi } from "../../redux/api";
import CustomForm from "../../components/CustomForm";
import ResponsiveContainer from "../../components/ResponsiveContainer";
import { useNavigate, useLocation } from "react-router-dom";
import { Routes } from "../../routes";
import { toast } from "react-hot-toast";
import { Typeahead } from "react-bootstrap-typeahead";
import useBankList from "../../hooks/useBankList";

function CreateContractor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { bankList } = useBankList();
  const contractor_code = location.state?.contractor_code || "";
  const isEditing = !!contractor_code;
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [form, setForm] = useState({
    contractor_name: "",
    contractor_phone: "",
    contractor_tin: "",
    contractor_email: "",
    contractor_address: "",
    rc_no: "",
    bank_name: "",
    account_name: "",
    account_number: "",
    sort_code: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditing) {
      setFetching(true);
      postApi("contractors/get", { contractor_code })
        .then((resp) => {
          if (resp.success && resp.result) {
            setForm({
              contractor_name: resp.result.contractor_name || "",
              contractor_phone: resp.result.contractor_phone || "",
              contractor_tin: resp.result.contractor_tin || "",
              contractor_email: resp.result.contractor_email || "",
              contractor_address: resp.result.contractor_address || "",
              rc_no: resp.result.rc_no || "",
              bank_name: resp.result.bank_name || "",
              account_name: resp.result.account_name || "",
              account_number: resp.result.account_number || "",
              sort_code: resp.result.sort_code || "",
            });
          } else {
            toast.error("Failed to load contractor data");
            navigate(Routes.contractorManagement.list);
          }
        })
        .catch(() => {
          toast.error("Error loading contractor");
          navigate(Routes.contractorManagement.list);
        })
        .finally(() => setFetching(false));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.contractor_name.trim()) errs.contractor_name = "Name is required";
    if (!form.contractor_phone.trim()) errs.contractor_phone = "Phone is required";
    if (!form.contractor_tin.trim()) errs.contractor_tin = "TIN is required";
    if (!form.rc_no.trim()) errs.rc_no = "RC No is required";
    if (!form.account_name.trim()) errs.account_name = "Account Name is required";
    if (!form.bank_name.trim()) errs.bank_name = "Bank Name is required";
    if (!form.account_number.trim()) errs.account_number = "Account Number is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const payload = isEditing ? { ...form, contractor_code } : form;
    const endpoint = isEditing ? "contractors/update" : "contractors/create";
    postApi(endpoint, payload)
      .then((resp) => {
        if (resp.success) {
          toast.success(isEditing ? "Contractor updated successfully" : `Contractor created with code: ${resp.contractor_code}`);
          navigate(Routes.contractorManagement.list);
        } else {
          toast.error(resp.error || "Operation failed");
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("An error occurred");
      })
      .finally(() => setLoading(false));
  };

  return (
    <ResponsiveContainer>
      <Card>
        <Card.Body>
          <Row className="align-items-center mb-3">
            <Col>
              <h5 className="mb-0">{isEditing ? "Edit Contractor" : "Create New Contractor"}</h5>
            </Col>
            <Col xs="auto">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(Routes.contractorManagement.list)}
              >
                Back to List
              </Button>
            </Col>
          </Row>

          <Form onSubmit={handleSubmit}>
            <h6 className="mb-3">Contractor Details</h6>
            <CustomForm
              handleChange={handleChange}
              fields={[
                {
                  label: "Name",
                  name: "contractor_name",
                  value: form.contractor_name,
                  required: true,
                  error: errors.contractor_name,
                },
                {
                  label: "Phone",
                  name: "contractor_phone",
                  value: form.contractor_phone,
                  required: true,
                  error: errors.contractor_phone,
                },
                {
                  label: "TIN Number",
                  name: "contractor_tin",
                  value: form.contractor_tin,
                  required: true,
                  error: errors.contractor_tin,
                },
                {
                  label: "Email (optional)",
                  name: "contractor_email",
                  value: form.contractor_email,
                },
                {
                  label: "Address (optional)",
                  name: "contractor_address",
                  value: form.contractor_address,
                },
                {
                  label: "RC No",
                  name: "rc_no",
                  value: form.rc_no,
                  required: true,
                  error: errors.rc_no,
                },
              ]}
            />

            <h6 className="mb-3 mt-4">Bank Details</h6>
            <Row>
              <Col md={4} className="mb-1">
                <FormLabel>
                  Account Name
                  <span className="text-danger ms-1">*</span>
                </FormLabel>
                <Form.Control
                  size="sm"
                  type="text"
                  name="account_name"
                  value={form.account_name}
                  onChange={handleChange}
                  isInvalid={!!errors.account_name}
                />
                {errors.account_name && (
                  <div className="invalid-feedback d-block">{errors.account_name}</div>
                )}
              </Col>
              <Col md={4} className="mb-1">
                <FormLabel>
                  Bank Name
                  <span className="text-danger ms-1">*</span>
                </FormLabel>
                <Typeahead
                  id="bank_name"
                  labelKey="title"
                  options={bankList}
                  placeholder="--select--"
                  selected={bankList.filter((item) => item.title === form.bank_name)}
                  onChange={(selected) =>
                    setForm((p) => ({ ...p, bank_name: selected.length > 0 ? selected[0].title : "" }))
                  }
                />
                {errors.bank_name && (
                  <div className="invalid-feedback d-block">{errors.bank_name}</div>
                )}
              </Col>
              <Col md={4} className="mb-1">
                <FormLabel>
                  Account Number
                  <span className="text-danger ms-1">*</span>
                </FormLabel>
                <Form.Control
                  size="sm"
                  type="text"
                  name="account_number"
                  value={form.account_number}
                  onChange={handleChange}
                  isInvalid={!!errors.account_number}
                />
                {errors.account_number && (
                  <div className="invalid-feedback d-block">{errors.account_number}</div>
                )}
              </Col>
              <Col md={4} className="mb-1">
                <FormLabel>Sort code</FormLabel>
                <Form.Control
                  size="sm"
                  type="text"
                  name="sort_code"
                  value={form.sort_code}
                  onChange={handleChange}
                />
              </Col>
            </Row>

            <div className="mt-4 d-flex gap-2">
              {fetching && (
                <div className="d-flex align-items-center">
                  <Spinner size="sm" className="me-2" />
                  Loading contractor data...
                </div>
              )}
              {!fetching && (
                <Button variant="success" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {isEditing ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    isEditing ? "Update Contractor" : "Create Contractor"
                  )}
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </ResponsiveContainer>
  );
}

export default CreateContractor;
